import moment from 'moment/moment';
import { getKnowledgeBaseConfig, setKnowledgeBaseConfig } from 'src/utils/pageStorageConfig';
import { uuid } from 'src/utils/uuid';
import { FILE_STATE } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';

const DATE_FORMAT = 'll';

export interface AutoSyncStatus {
  status: string;
  color: string;
}

export interface TreeNode {
  id?: string;
  name: string;
  type: string;
  path: string;
  root?: boolean;
  parent?: TreeNode | false | string;
  children?: TreeNode[];
  visible?: boolean;
  checked?: boolean;
  disabled?: boolean;
  autosync?: AutoSyncStatus;
  localDate?: string;
  updated_at?: string;
  created_at?: string;
  state?: string;
  collector_id?: string | null;
  data?: Integration;
  urls?: string[];
  newPath?: string;
  [key: string]: unknown;
}

export interface Integration {
  id: string;
  name: string;
  type: string;
  enabled?: boolean;
  schedule?: { enabled?: boolean };
  updated_at?: string;
  created_at?: string;
  [key: string]: unknown;
}

export interface KBFile {
  name: string;
  path?: string;
  state?: string;
  collector_id?: string | null;
  updated_at?: string;
  [key: string]: unknown;
}

export const SORT_MAP = {
  type: 'By Type',
  name: 'By Name',
  date: 'By Date',
} as const;

export const SORT_ORDER: Record<string, number> = {
  integration: 40,
  website: 30,
  folder: 20,
  FAQ: 10,
  file: 0,
};

export const INTEGRATION_NAMES_MAP: Record<string, string> = {
  zendesk_helpcenter: 'Zendesk Help Center',
  zendesk_tickets: 'Zendesk Tickets',
  freshdesk_helpcenter: 'Freshdesk Help Center',
  freshdesk_tickets: 'Freshdesk Tickets',
  web_scrapper: 'Website',
};

export const AUTOSYNC_STATUSES_MAP = {
  unavailable: {
    status: 'Unavailable',
    color: '#C1CACB',
  },
  active: {
    status: 'Active',
    color: '#33FF71',
  },
  paused: {
    status: 'Paused',
    color: '#FFBA33',
  },
  failed: {
    status: 'Failed',
    color: '#FF3F33',
  },
} as const;

const getIntegrationAutoSyncStatus = (integration: Integration): AutoSyncStatus => {
  const map = AUTOSYNC_STATUSES_MAP;

  switch (integration.type) {
    case 'zendesk_helpcenter':
      return integration?.enabled ? map.active : map.paused;
    case 'zendesk_tickets':
    case 'freshdesk_tickets':
    case 'web_scrapper': {
      return integration?.schedule?.enabled ? map.active : map.paused;
    }
    default: {
      return map.active;
    }
  }
};

export const getInitialTree = (): TreeNode => {
  return {
    name: 'Knowledge Base',
    type: 'folder',
    path: '/',
    root: true,
    parent: false,
    children: [],
  };
};

export const getFileExtension = (path: string): string => {
  const cleaned = path.replace(/[\\/]/g, '');
  const match = cleaned.match(/\.([^.]+)$/);

  return match ? match[1] : '';
};

function findWhere(array: TreeNode[], key: string, value: unknown): TreeNode | false {
  let t = 0;
  while (t < array.length && (array[t] as Record<string, unknown>)[key] !== value) {
    t++;
  }

  return t < array.length ? array[t] : false;
}

export const getFoldersStructure = (
  files: KBFile[] = [],
  integrations: Integration[] = [],
): TreeNode => {
  const tree = getTreeFromFiles(files);
  addIntegrationsToTree(files, tree, integrations);

  tree.root = true;
  mergeWithEmptyFolders(tree);
  setUpdatedAtFolders(tree);

  return tree;
};

export const addIntegrationsToTree = (
  files: KBFile[] | null,
  tree: TreeNode,
  integrations: Integration[],
): void => {
  files = files || [];

  const rootChildren = tree.children!;

  integrations.forEach((integration) => {
    const autosync = getIntegrationAutoSyncStatus(integration);
    const treeItem: TreeNode = {
      name: integration.name,
      parent: tree,
      type: integration.type === 'web_scrapper' ? 'website' : 'integration',
      autosync: autosync,
      disabled: false,
      visible: true,
      path: `/${integration.name}`.replace(/\/\//, '/'),
      localDate: getLocalFileDate(integration.updated_at),
      id: integration.id,
      updated_at: integration.updated_at,
      created_at: integration.created_at,
      data: integration,
      children: [],
    };

    treeItem.children = files
      .filter((file) => {
        return file.collector_id === integration.id;
      })
      .map((file) => {
        return {
          ...file,
          parent: treeItem,
          type: treeItem.type,
          autosync: autosync,
          visible: true,
          disabled: file.state !== 'uploaded',
          path: `${treeItem.path}/${file.name}`.replace(/\/\//, '/'),
          localDate: getLocalFileDate(file.updated_at),
        } as TreeNode;
      });

    rootChildren.push(treeItem);
  });
};

export const getTreeFromFiles = (files: KBFile[]): TreeNode => {
  const tree: TreeNode[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const isInRoot = !Boolean(file.path) || file.path === '/';
    const path = isInRoot ? [''] : `/${file.path}`.replace(/\/\//, '/').split('/');
    let currentLevel: TreeNode[] = tree;
    let parent: TreeNode | undefined;

    path.push(file.name);

    for (let j = 0; j < path.length; j++) {
      const isLastItem = j === path.length - 1;
      const part = path[j];
      const ext = getFileExtension(part);
      const existingPath = findWhere(currentLevel, 'name', part);

      if (existingPath) {
        if (isLastItem) {
          currentLevel.push({
            id: `${file.name}-${parent?.name || ''}-${uuid()}`,
            ...file,
            parent,
            name: ext === 'faq' ? file.name.split(`.${ext}`)[0].trim() : file.name,
            type: ext === 'faq' ? 'FAQ' : 'file',
            visible: true,
            autosync: AUTOSYNC_STATUSES_MAP.unavailable,
            disabled: file.state !== 'uploaded',
            path: `${file.path || ''}/${file.name}`.replace(/\/\//, '/'),
            localDate: getLocalFileDate(file.updated_at),
          } as TreeNode);
        }

        currentLevel = existingPath.children!;
        parent = existingPath;
      } else {
        let newPart: TreeNode;

        if (isLastItem) {
          newPart = {
            id: `${file.name}-${parent?.name || ''}-${uuid()}`,
            ...file,
            parent,
            name: ext === 'faq' ? file.name.split(`.${ext}`)[0].trim() : file.name,
            type: ext === 'faq' ? 'FAQ' : 'file',
            visible: true,
            autosync: AUTOSYNC_STATUSES_MAP.unavailable,
            disabled: file.state !== 'uploaded',
            path: `${file.path || ''}/${file.name}`.replace(/\/\//, '/'),
            localDate: getLocalFileDate(file.updated_at),
          } as TreeNode;
        } else {
          newPart = {
            id: `${part}-${uuid()}`,
            name: part,
            parent,
            type: 'folder',
            visible: true,
            autosync: AUTOSYNC_STATUSES_MAP.unavailable,
            path: parent ? `${parent.path}/${part}`.replace(/\/\//, '/') : '/',
            children: [],
          };
        }

        if (file.collector_id === null) {
          currentLevel.push(newPart);
          currentLevel = newPart.children!;
          parent = newPart;
        }
      }
    }
  }

  return tree.length ? tree[0] : getInitialTree();
};

export const sortByType = (array: TreeNode[]): TreeNode[] => {
  return array
    .sort((a, b) => a.name.localeCompare(b.name))
    .sort((a, b) => {
      if (SORT_ORDER[a.type] > SORT_ORDER[b.type]) {
        return -1;
      } else {
        return 1;
      }
    })
    .sort((a, b) => {
      if (SORT_ORDER[a.type] === SORT_ORDER[b.type]) {
        return a.name.localeCompare(b.name);
      } else {
        return 0;
      }
    });
};

export const sortByName = (array: TreeNode[]): TreeNode[] => {
  return array.sort((a, b) => {
    if (a.name < b.name) {
      return -1;
    }
    if (a.name > b.name) {
      return 1;
    }
    return 0;
  });
};

export const sortByDate = (array: TreeNode[]): TreeNode[] => {
  return array
    .sort((a, b) => {
      return (
        Number(moment(b.updated_at).format('YYYYMMDD')) -
        Number(moment(a.updated_at).format('YYYYMMDD'))
      );
    })
    .sort((a) => (a.type === 'folder' ? -1 : 1));
};

export const createFolder = (name: string, parent: TreeNode): TreeNode => {
  const folder: TreeNode = {
    id: `${name}-${uuid()}`,
    name,
    parent: parent,
    type: 'folder',
    visible: true,
    autosync: AUTOSYNC_STATUSES_MAP.unavailable,
    path: `${parent ? parent.path : ''}/${name}`.replace(/\/\//, '/'),
    localDate: getLocalFileDate(),
    children: [],
  };
  const config = getKnowledgeBaseConfig() as Record<string, unknown>;

  config.emptyFolders = (config.emptyFolders as TreeNode[]) || [];

  const isLocalExist = (config.emptyFolders as TreeNode[]).some(
    (emptyFolder) => emptyFolder.path === folder.path,
  );

  if (!isLocalExist) {
    const localFolder = { ...folder, parent: parent.path };

    (config.emptyFolders as TreeNode[]).push(localFolder as TreeNode);
    setKnowledgeBaseConfig(config);
  }

  return folder;
};

export const createFile = (file: KBFile, parent: TreeNode): TreeNode => {
  const ext = getFileExtension(file.name);
  const isFAQ = ext === 'faq';

  return {
    id: `${file.name}-${parent.name}-${uuid()}`,
    ...file,
    parent,
    visible: true,
    name: isFAQ ? file.name.split(`.${ext}`)[0].trim() : file.name,
    type: isFAQ ? 'FAQ' : 'file',
    autosync: parent?.autosync || AUTOSYNC_STATUSES_MAP.unavailable,
    path: `${file.path}/${file.name}`,
    localDate: getLocalFileDate(file.updated_at),
  } as TreeNode;
};

export const updateBranchInTree = (
  tree: TreeNode,
  branch: TreeNode,
  newBranch: Partial<TreeNode>,
): TreeNode | void => {
  if (tree.path === branch.path) {
    Object.assign(tree, { ...newBranch });
    mergeWithEmptyFolders(tree);

    return tree;
  } else {
    for (let i = 0; i < tree.children!.length; i++) {
      if (tree.children![i].type === 'folder') {
        updateBranchInTree(tree.children![i], branch, newBranch);
      }
    }
  }
};

export const findBranch = (tree: TreeNode, path: string): TreeNode | false | undefined => {
  if (!path) {
    return false;
  }

  if (tree.path === path || (tree.urls && tree.urls[0] === path)) {
    return tree;
  }

  if (tree.children && tree.children.length) {
    for (let i = 0; i < tree.children.length; i++) {
      const is = {
        folder: tree.children[i].type === 'folder',
        website: tree.children[i].type === 'website' && !tree.children[i].collector_id,
        integration: tree.children[i].type === 'integration' && !tree.children[i].collector_id,
      };

      if (is.folder || is.integration || is.website) {
        const result = findBranch(tree.children[i], path);

        if (result) {
          return result;
        }
      }
    }
  }
};

export const findActiveBranch = (tree: TreeNode): TreeNode | false | undefined => {
  const { activeBranchPath } = getKnowledgeBaseConfig() as { activeBranchPath?: string };

  if (!activeBranchPath) {
    return false;
  }

  return findBranch(tree, activeBranchPath);
};

export const getLocalFileDate = (
  date: Date | string = new Date(),
  format: string = DATE_FORMAT,
): string => {
  const utcDate = moment.utc(date).toDate();

  return moment(utcDate).format(format);
};

export const getFoldersArrayFromTree = (tree: TreeNode): TreeNode[] => {
  let folders: TreeNode[] = [];

  if (tree.type === 'folder') {
    folders.push(tree);
  }

  if (tree.children && tree.children.length) {
    for (let i = 0; i < tree.children.length; i++) {
      const children = tree.children[i];

      if (children.type === 'folder') {
        const childFolders = getFoldersArrayFromTree(children);

        folders = [...folders, ...childFolders];
      }
    }
  }

  return folders;
};

export const getCheckedFiles = (branch: TreeNode): TreeNode[] => {
  let folders: TreeNode[] = [];

  if (branch.visible && branch.checked) {
    folders.push(branch);
  }

  for (let i = 0; i < branch.children!.length; i++) {
    const children = branch.children![i];

    if (children.visible && children.type === 'folder') {
      const childFolders = getCheckedFiles(children);

      folders = folders.concat(childFolders);
    } else if (children.visible && children.checked) {
      folders.push(children);
    }
  }

  return folders;
};

export const setCheckedChildren = (branch: TreeNode, checked?: boolean, parent = true): void => {
  checked = checked === undefined ? branch.checked : checked;

  if (branch.visible && !branch.root && branch.type !== 'integration' && !parent) {
    branch.checked = checked;
  }

  for (let i = 0; i < branch.children!.length; i++) {
    const children = branch.children![i];

    if (children.visible && children.type !== 'integration') {
      children.checked = checked;
    }

    if (children.visible && children.type === 'folder') {
      setCheckedChildren(children, checked, false);
    }
  }
};

export const updateCheckedPaths = (files: TreeNode[], path: string): TreeNode[] => {
  return files.map((file) => {
    if (file.parent && typeof file.parent !== 'string' && file.parent.checked) {
      file.newPath = `${file.parent.newPath}/${file.name}`;
    } else {
      file.newPath = `${path}/${file.name}`;
    }

    file.newPath = file.newPath.replace(/\/\//, '/');

    return file;
  });
};

export const getRootFolder = (item: TreeNode): TreeNode | undefined => {
  let result: TreeNode | undefined;

  if (item.root) {
    result = item;
  }

  if (item.parent && typeof item.parent !== 'string') {
    result = getRootFolder(item.parent);
  }

  return result;
};

export const getChildrenFiles = (item: TreeNode): TreeNode[] => {
  let files: TreeNode[] = [];

  if (item.children && item.children.length) {
    for (let i = 0; i < item.children.length; i++) {
      const children = item.children[i];

      if (children.type === 'folder') {
        const childFolders = getChildrenFiles(children);

        files = files.concat(childFolders);
      } else {
        files.push(children);
      }
    }
  }

  return files;
};

export const getItemsByName = (name = '', branch: TreeNode): TreeNode[] => {
  let items: TreeNode[] = [];

  for (let i = 0; i < branch.children!.length; i++) {
    const children = branch.children![i];

    if (['folder', 'integration', 'website'].includes(children.type) && children.children) {
      const childFolders = getItemsByName(name, children);

      items = items.concat(childFolders);
    }

    if (children.name.toLowerCase().includes(name.trim().toLowerCase())) {
      items.push(children);
      continue;
    }

    if (children.url && typeof children.url === 'string') {
      if (children.url.toLowerCase().includes(name.trim().toLowerCase())) {
        items.push(children);
      }
    }
  }

  return items;
};

export const filterBranchByName = (name: string, branch: TreeNode): TreeNode => {
  const newBranch = { ...branch };
  newBranch.children = getItemsByName(name, newBranch);

  return newBranch;
};

export const getParentsFolders = (branch: TreeNode): TreeNode[] => {
  let items: TreeNode[] = [];

  items.unshift(branch);

  if (branch.parent && typeof branch.parent !== 'string') {
    const parent = getParentsFolders(branch.parent);
    items = parent.concat(items);
  }

  return items;
};

export const setUpdatedAtFolders = (branch: TreeNode): void => {
  if (branch.children) {
    for (let i = 0; i < branch.children.length; i++) {
      setUpdatedAtFolders(branch.children[i]);
    }
  }

  if (branch.parent && typeof branch.parent !== 'string') {
    if (branch.parent.updated_at) {
      const isAfter = moment(branch.updated_at).isSameOrAfter(branch.parent.updated_at);

      branch.parent.updated_at = isAfter ? branch.updated_at : branch.parent.updated_at;
    } else {
      branch.parent.updated_at = branch.updated_at;
    }

    branch.parent.localDate = getLocalFileDate(branch.parent.updated_at);
  }
};

export const mergeWithEmptyFolders = (branch: TreeNode): void => {
  const config = getKnowledgeBaseConfig() as Record<string, unknown>;
  config.emptyFolders = (config.emptyFolders as TreeNode[]) || [];

  (config.emptyFolders as TreeNode[]).forEach((folder) => {
    const parent = findBranch(branch, folder.parent as string);

    if (parent && typeof parent !== 'boolean') {
      const child = parent.children!.find((child) => child.path === folder.path);

      if (child) {
        const childFiles = getChildrenFiles(child);
        if (childFiles.length) {
          config.emptyFolders = (config.emptyFolders as TreeNode[]).filter(
            (emptyFolder) => emptyFolder.path !== folder.path,
          );

          setKnowledgeBaseConfig(config);
        }
      } else {
        parent.children!.push({ ...folder, parent });
      }
    }
  });
};

export const setVisibilityByTypes = (branch: TreeNode, types: Array<{ type: string }>): void => {
  branch?.children?.forEach((child) => {
    if (child.type === 'folder') {
      child.visible = true;

      return;
    }

    child.visible = types.some(({ type }) => type.toLowerCase() === child.type.toLowerCase());
  });
};

export const removeEmptyFolder = (folder: TreeNode): void => {
  const config = getKnowledgeBaseConfig() as Record<string, unknown>;

  config.emptyFolders = ((config.emptyFolders as TreeNode[]) || []).filter(
    (emptyFolder) =>
      emptyFolder.path !== folder.path && !emptyFolder.path.startsWith(`${folder.path}/`),
  );

  setKnowledgeBaseConfig(config);
};

export const removeEmptyFolders = (folders: TreeNode[]): TreeNode[] => {
  folders.forEach(removeEmptyFolder);

  return (getKnowledgeBaseConfig().emptyFolders as TreeNode[]) || [];
};

export const addFolderToEmptyFolders = (branch: TreeNode): void => {
  if (branch.type === 'folder') {
    const newFolder: Record<string, unknown> = {
      ...branch,
      children: [],
      path: branch.newPath || branch.path,
      checked: false,
    };

    if (branch.parent) {
      const parent = (newFolder.path as string).split('/');

      newFolder.parent = parent.slice(0, parent.length - 1).join('/') || '/';
    }

    const config = getKnowledgeBaseConfig() as Record<string, unknown>;

    config.emptyFolders = (config.emptyFolders as TreeNode[]) || [];

    const isEmptyFolderExist = (config.emptyFolders as TreeNode[]).some(
      (emptyFolder) => emptyFolder.path === newFolder.path,
    );

    if (!newFolder.root && !isEmptyFolderExist) {
      config.emptyFolders = removeEmptyFolders([branch]);
      (config.emptyFolders as TreeNode[]).push(newFolder as TreeNode);
      setKnowledgeBaseConfig(config);
    }
  }

  if (branch.parent && typeof branch.parent !== 'string') {
    addFolderToEmptyFolders(branch.parent);
  }
};

export const editEmptyFolder = (name: string, item: TreeNode): void => {
  const config = getKnowledgeBaseConfig() as Record<string, unknown>;

  config.emptyFolders = (config.emptyFolders as TreeNode[]) || [];

  const emptyFolder = (config.emptyFolders as TreeNode[]).find(
    (folder) => folder.path === item.path,
  );
  const pathParts = item.path.split('/');

  const newPath = pathParts.slice(0, pathParts.length - 1).join('/') || '/';
  if (emptyFolder) {
    emptyFolder.name = name;
    emptyFolder.path = `${newPath}/${name}`.replace(/\/\//, '/');
  }
  setKnowledgeBaseConfig(config);
};

export const isUploadingState = (item: TreeNode): boolean => {
  if (item.state === FILE_STATE.UPLOADING || item.state === FILE_STATE.DELETING) {
    return true;
  }

  if (item.children && item.children.length) {
    for (let i = 0; i < item.children.length; i++) {
      const children = item.children[i];

      const isUploading = isUploadingState(children);

      if (isUploading) {
        return true;
      }
    }
  }

  return false;
};

export const removeFileName = (path: string, fileName: string) => {
  if (!path || !fileName) return path || '';

  const escape = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  return path.replace(/\\/g, '/').replace(new RegExp(`/${escape(fileName)}$`), '');
};

export function generateUniqueName(name, items) {
  const existingNames = new Set(items.map((item) => item.name));

  if (!existingNames.has(name)) {
    return name;
  }

  let count = 1;
  let uniqueName = `${name}-${count}`;

  while (existingNames.has(uniqueName)) {
    count += 1;
    uniqueName = `${name}-${count}`;
  }

  return uniqueName;
}
